/**
 * "4 invoices selected" — reaching the ear, not just the eye.
 *
 *   tick a checkbox ──► enhancedGridState.selectedRows ──► selectedCount
 *                              │  (debounced 250ms by useGridRows)
 *                              ▼
 *                     count CHANGED ?  ──►  ONE utterance
 *
 * WHY THE COUNT AND NOT THE BAR: `BulkActionToolbar` mounts with its text
 * already in it, and a live region that appears together with its content is
 * routinely missed — the region has to pre-exist the change. It is also
 * conditional on `bulkActions` being supplied, while selection is not. So the
 * region lives in the always-mounted toolbar and watches the count.
 *
 * WHY THIS IS NOT NOISE: the count only moves when the user ticks, unticks,
 * or clears — one utterance per deliberate act — and `useGridRows` already
 * debounces `selectedRows`, so a select-all lands as one number, not fifty.
 */
import { useEffect, useRef, useState } from 'react';
import {
  type GridAnnouncement,
  gridSelectionAnnouncement,
  gridSelectionNouns,
  nextAnnouncement,
  SILENT_ANNOUNCEMENT,
} from './gridAnnouncements';

export type GridSelectionAnnouncementOptions = {
  selectedCount: number;
  entityName?: string;
  entityNamePlural?: string;
};

export function useGridSelectionAnnouncement({
  selectedCount,
  entityName,
  entityNamePlural,
}: GridSelectionAnnouncementOptions): GridAnnouncement {
  const [announcement, setAnnouncement] = useState(SILENT_ANNOUNCEMENT);
  /** Seeded from the first render, so a grid that mounts with rows already ticked stays quiet. */
  const lastCount = useRef(selectedCount);

  useEffect(() => {
    const previous = lastCount.current;

    if (previous === selectedCount) return;

    lastCount.current = selectedCount;

    const text = gridSelectionAnnouncement(previous, selectedCount, gridSelectionNouns(entityName, entityNamePlural));

    setAnnouncement((current) => nextAnnouncement(current, text));
  }, [selectedCount, entityName, entityNamePlural]);

  return announcement;
}
